import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { getFilterPanelSlotStyle } from './filterPanelVisibleState';
import { dispatchTableScrollYRemeasure } from './useTableScrollY';

function FilterPanelSlotInner({ visible, children }) {
    return <div style={getFilterPanelSlotStyle(visible)}>{children}</div>;
}

/**
 * 列表页筛选面板显隐：配合 PageHeader `showFilterToggle` 与 FilterPanel/FilterForm 包裹。
 *
 * @param {object} [options]
 * @param {boolean} [options.defaultVisible=true]
 * @param {boolean} [options.remeasureScrollY=true] - 显隐变化时触发 resize，供 useTableScrollY 重算
 */
export function useFilterPanelVisible(options = {}) {
    const { defaultVisible = true, remeasureScrollY = true } = options;
    const [filterVisible, setFilterVisible] = useState(defaultVisible);
    const didMountRef = useRef(false);
    const filterVisibleRef = useRef(filterVisible);
    filterVisibleRef.current = filterVisible;

    // biome-ignore lint/correctness/useExhaustiveDependencies: 筛选面板显隐变化需触发 scroll.y 重算
    useLayoutEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        if (!remeasureScrollY) return;
        dispatchTableScrollYRemeasure();
    }, [filterVisible, remeasureScrollY]);

    const filterToggleProps = useMemo(
        () => ({
            showFilterToggle: true,
            filterVisible,
            onFilterVisibleChange: setFilterVisible,
        }),
        [filterVisible],
    );

    const FilterPanelSlot = useMemo(() => {
        return function FilterPanelSlot({ children }) {
            return <FilterPanelSlotInner visible={filterVisibleRef.current}>{children}</FilterPanelSlotInner>;
        };
    }, []);

    return {
        filterVisible,
        setFilterVisible,
        filterToggleProps,
        FilterPanelSlot,
    };
}
